import React from "react";
import { AdminApis } from "../../apis/adminApi";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ToastContainer, toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import configs from "../../configs";
import CardPageVisits from "./CardPageVisits";
import CardRenewSubscription from "./CardRenewSubscription";
import {
  FiCopy as FiCopyIcon, FiEdit2 as FiEdit2Icon, FiTrash2, FiExternalLink,
  FiLink, FiMousePointer, FiCheckCircle, FiAlertTriangle, FiPlus,
  FiSearch, FiX, FiArrowRight, FiGlobe,
} from "react-icons/fi";

export default function CardRedirectLink() {
  /* ── List state ───────────────────────────────────────────────── */
  const [effect, setEffect] = React.useState("");
  const [loader, setLoader] = React.useState(true);
  const [data, setData] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");

  /* ── Create modal ─────────────────────────────────────────────── */
  const [createVisible, setCreateVisible] = React.useState(false);
  const [createName, setCreateName] = React.useState("");
  const [createUrl, setCreateUrl] = React.useState("");
  const [nameExist, setNameExist] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  /* ── Edit modal ───────────────────────────────────────────────── */
  const [editVisible, setEditVisible] = React.useState(false);
  const [selectedLink, setSelectedLink] = React.useState(null);
  const [editUrl, setEditUrl] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  /* ── Delete modal ─────────────────────────────────────────────── */
  const [deleteVisible, setDeleteVisible] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  /* ── Body scroll lock ─────────────────────────────────────────── */
  const anyModal = createVisible || editVisible || deleteVisible;
  React.useEffect(() => {
    document.body.style.overflow = anyModal ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [anyModal]);

  /* ── Fetch links ──────────────────────────────────────────────── */
  React.useEffect(() => {
    setLoader(true);
    AdminApis.getRedirectLinks()
      .then((res) => { if (res?.data) setData(res.data); })
      .finally(() => setLoader(false));
  }, [effect]);

  /* ── Name availability ────────────────────────────────────────── */
  React.useEffect(() => {
    if (!createName) return;
    const clean = cleanName(createName);
    AdminApis.searchName({ name: clean }).then((res) => {
      if (res?.data) setNameExist(res.data.data);
    });
  }, [createName]);

  const cleanName = (n) => n.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");

  const openCreate = () => {
    setCreateName(""); setCreateUrl(""); setNameExist("");
    setCreateVisible(true);
  };
  const openEdit = (link) => {
    setSelectedLink(link);
    setEditUrl(link?.link_info?.message || link?.url || "");
    setEditVisible(true);
  };
  const openDelete = (link) => { setSelectedLink(link); setDeleteVisible(true); };

  const getClickCount = (link) =>
    link?.short_url?.visits?.filter((v) => v?.operating_system !== "0").length || 0;

  /* ── Create submit ────────────────────────────────────────────── */
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    const fd = new FormData();
    fd.append("name", cleanName(createName));
    fd.append("url", createUrl.trim());
    try {
      const res = await AdminApis.createRedirectLink(fd);
      if (res?.data) {
        setCreateVisible(false);
        setEffect("c_" + Date.now());
        toast.success(res.data.message || "Redirect link created!");
      } else {
        toast.error(res?.response?.data?.message || "Link name already in use.");
      }
    } catch { toast.error("Failed to create link."); }
    finally { setCreating(false); }
  };

  /* ── Edit submit ──────────────────────────────────────────────── */
  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await AdminApis.editLink({
        message: editUrl,
        id: selectedLink?.id,
        name: selectedLink?.name,
        phone_number: selectedLink?.link_info?.phone_number,
      });
      if (res?.data) {
        setEditVisible(false);
        setEffect("v_" + Date.now());
        toast.success(res.data.message || "Link updated!");
      }
    } catch { toast.error("Failed to update link."); }
    finally { setSaving(false); }
  };

  /* ── Delete submit ────────────────────────────────────────────── */
  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      const res = await AdminApis.deleteLink(selectedLink?.id);
      if (res?.data) {
        setDeleteVisible(false);
        setEffect("d_" + Date.now());
        toast.success("Link deleted.");
      }
    } catch { toast.error("Failed to delete link."); }
    finally { setDeleting(false); }
  };

  /* ── Filtered list ────────────────────────────────────────────── */
  const filteredLinks = React.useMemo(() => {
    const links = data?.redirect_links || [];
    if (!searchTerm.trim()) return links;
    return links.filter((l) =>
      Object.values(l).join(" ").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  /* ── Skeleton ─────────────────────────────────────────────────── */
  if (loader) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between">
            <div className="h-8 bg-gray-100 rounded-xl w-44" />
            <div className="h-10 bg-gray-200 rounded-2xl w-40" />
          </div>
          <div className="h-10 bg-gray-100 rounded-xl w-full max-w-sm" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="h-12 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-gray-100 rounded-full w-3/4" />
                  <div className="h-10 bg-gray-100 rounded-xl" />
                  <div className="h-3 bg-gray-100 rounded-full w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (data?.data === "sub_expired") return <CardRenewSubscription />;

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
              <FiArrowRight className="text-[#0071BC]" size={17} />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">My Redirect Links</h1>
              <p className="text-xs text-gray-400 mt-0.5">
                {(data?.redirect_links?.length || 0)} {(data?.redirect_links?.length || 0) === 1 ? "link" : "links"} created
              </p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0071BC] text-white text-sm font-bold rounded-2xl hover:bg-[#005fa0] active:scale-95 transition-all shadow-sm hover:shadow-md self-start sm:self-auto"
          >
            <FiPlus size={16} strokeWidth={2.5} />
            Create New Link
          </button>
        </div>

        {/* Info banner */}
        <div className="mt-3 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-2xl px-3 py-2.5">
          <FiGlobe className="text-[#0071BC] flex-shrink-0 mt-0.5" size={13} />
          <p className="text-xs text-gray-600 leading-relaxed">
            Create custom short links for easy sharing. Track clicks with analytics, and share any URL behind a clean, branded link.
          </p>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <div className="px-3 sm:px-4 py-5">

        {/* Search */}
        {(data?.redirect_links?.length || 0) > 0 && (
          <div className="relative max-w-sm mb-5">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
            <input
              type="text"
              placeholder="Search links…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all shadow-sm"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <FiX size={12} />
              </button>
            )}
          </div>
        )}

        {/* Cards */}
        {filteredLinks.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredLinks.map((link) => {
              const clicks = getClickCount(link);
              const shortUrl = `${configs?.baseRedirect}/${link?.name}`;
              const destUrl = link?.link_info?.message || link?.url || "";
              return (
                <div key={link.id} className="group bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col">

                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-[#0071BC]">
                    <div className="flex items-center gap-2 min-w-0">
                      <FiArrowRight className="text-white/70 flex-shrink-0" size={12} />
                      <span className="text-white font-bold text-sm truncate">{link?.name}</span>
                    </div>
                    <CopyToClipboard text={shortUrl} onCopy={() => toast.success("Copied!")}>
                      <button className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-[11px] font-semibold rounded-full border border-white/30 transition-all active:scale-95">
                        <FiCopyIcon size={10} /> Copy
                      </button>
                    </CopyToClipboard>
                  </div>

                  {/* Body */}
                  <NavLink to={`/link-details/${link?.id}`} className="flex-1 flex flex-col p-4 gap-3 hover:bg-gray-50/50 transition-colors">

                    {/* Click count */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-medium">Short link</span>
                      <div className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${clicks > 0 ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                        <FiMousePointer size={9} />
                        {clicks} click{clicks !== 1 ? "s" : ""}
                      </div>
                    </div>

                    {/* Short URL */}
                    <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                      <FiLink className="text-[#0071BC] flex-shrink-0" size={11} />
                      <span className="text-xs text-[#0071BC] font-semibold truncate">{shortUrl}</span>
                    </div>

                    {/* Arrow */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-100" />
                      <FiArrowRight className="text-gray-300" size={12} />
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>

                    {/* Destination URL */}
                    <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                      <FiGlobe className="text-gray-400 flex-shrink-0" size={11} />
                      <span className="text-xs text-gray-500 truncate">{destUrl || <span className="italic text-gray-300">No destination set</span>}</span>
                    </div>
                  </NavLink>

                  {/* Footer */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                    <span className="flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
                      <FiArrowRight size={9} /> Redirect
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(link)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-[#0071BC] hover:bg-blue-50 transition-all" title="Edit">
                        <FiEdit2Icon size={13} />
                      </button>
                      <button onClick={() => openDelete(link)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all" title="Delete">
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (data?.redirect_links?.length || 0) === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center mb-5 shadow-inner">
              <FiArrowRight className="text-[#0071BC]" size={32} />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 mb-2">No redirect links yet</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-xs leading-relaxed">
              Create a short branded link that redirects visitors to any URL you choose.
            </p>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-6 py-3 bg-[#0071BC] text-white text-sm font-bold rounded-2xl hover:bg-[#005fa0] active:scale-95 transition-all shadow-md"
            >
              <FiPlus size={15} /> Create First Link
            </button>
          </div>
        ) : (
          /* No search results */
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-3xl bg-gray-100 flex items-center justify-center mb-4">
              <FiSearch className="text-gray-300" size={26} />
            </div>
            <h3 className="text-base font-extrabold text-gray-900 mb-1">No links found</h3>
            <p className="text-gray-400 text-sm mb-4">Try a different search term.</p>
            <button onClick={() => setSearchTerm("")} className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 shadow-sm transition-colors">
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          CREATE MODAL
      ══════════════════════════════════════════════════════════════ */}
      {createVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCreateVisible(false)} />
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
                  <FiArrowRight className="text-[#0071BC]" size={16} />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-gray-900">Create Redirect Link</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Short link → any destination URL</p>
                </div>
              </div>
              <button onClick={() => setCreateVisible(false)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
                <FiX size={15} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleCreate} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

                {/* Link name */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    Link Name <span className="text-gray-400 font-normal">(Keep it short)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      placeholder="Eg. mark-survey-form"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all pr-10"
                    />
                    <FiSearch className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#0071BC]" size={14} />
                  </div>

                  {createName && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${nameExist > 0 ? "bg-red-50 text-red-600 border border-red-100" : "bg-green-50 text-green-700 border border-green-100"}`}>
                        {nameExist > 0 ? <FiX size={10} /> : <FiCheckCircle size={10} />}
                        {nameExist > 0 ? "Name taken" : "Available"}
                      </span>
                      <span className="text-xs text-gray-400">link.mygupta.co/<strong className="text-gray-600">{cleanName(createName)}</strong></span>
                    </div>
                  )}
                  {!createName && (
                    <p className="text-xs text-gray-400 mt-1.5">link.mygupta.co/<span className="text-gray-500">your-name</span></p>
                  )}
                </div>

                {/* Destination URL */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Destination URL</label>
                  <div className="relative">
                    <FiGlobe className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                      type="url"
                      required
                      placeholder="https://example.com/your-page"
                      value={createUrl}
                      onChange={(e) => setCreateUrl(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1.5">Anyone who visits your short link will be redirected to this URL.</p>
                </div>

                {/* Visual preview */}
                {(createName || createUrl) && (
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">Preview</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-[#0071BC] truncate">link.mygupta.co/{cleanName(createName) || "your-name"}</span>
                      <FiArrowRight className="text-gray-300 flex-shrink-0" size={12} />
                      <span className="text-gray-500 truncate">{createUrl || "destination"}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex gap-2.5 flex-shrink-0">
                <button type="button" onClick={() => setCreateVisible(false)} className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-all">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || nameExist === 1}
                  className="flex-1 py-3 text-sm font-bold text-white bg-[#0071BC] hover:bg-[#005fa0] rounded-2xl transition-all shadow-md active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {creating ? "Creating…" : "Create Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          EDIT MODAL
      ══════════════════════════════════════════════════════════════ */}
      {editVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditVisible(false)} />
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
                  <FiEdit2Icon className="text-[#0071BC]" size={14} />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-gray-900">Edit Redirect Link</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Link: <span className="font-semibold text-[#0071BC]">{selectedLink?.name}</span></p>
                </div>
              </div>
              <button onClick={() => setEditVisible(false)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
                <FiX size={15} />
              </button>
            </div>
            <form onSubmit={handleEdit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Destination URL</label>
                  <div className="relative">
                    <FiGlobe className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                      type="text"
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      placeholder="https://example.com/your-page"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1.5">Visitors will be redirected to this URL when they click your link.</p>
                </div>

                {/* Short link read-only */}
                <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3">
                  <FiLink className="text-[#0071BC] flex-shrink-0" size={14} />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Short Link</p>
                    <p className="text-sm font-semibold text-[#0071BC] mt-0.5">link.mygupta.co/{selectedLink?.name}</p>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex gap-2.5 flex-shrink-0">
                <button type="button" onClick={() => setEditVisible(false)} className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-all">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-3 text-sm font-bold text-white bg-[#0071BC] hover:bg-[#005fa0] rounded-2xl transition-all shadow-md active:scale-[0.98] disabled:opacity-60">
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          DELETE MODAL
      ══════════════════════════════════════════════════════════════ */}
      {deleteVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteVisible(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-5 py-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <FiAlertTriangle className="text-red-500" size={18} />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-extrabold text-gray-900">Delete Link?</h2>
                <p className="text-sm text-gray-500 mt-0.5"><span className="font-semibold text-gray-700">{selectedLink?.name}</span> will be permanently removed.</p>
              </div>
              <button onClick={() => setDeleteVisible(false)} className="w-7 h-7 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 transition-all flex-shrink-0">
                <FiX size={13} />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">What happens:</p>
              <ul className="space-y-2">
                {["The link immediately stops working", "All click analytics will be lost", "The link name becomes available to others", "Visitors will be redirected to gupta.link"].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FiX className="text-red-500" size={8} strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 pb-5 flex gap-2.5">
              <button type="button" onClick={() => setDeleteVisible(false)} className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-all">Keep Link</button>
              <form onSubmit={handleDelete} className="flex-1">
                <button type="submit" disabled={deleting} className="w-full py-3 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-2xl transition-all shadow-md active:scale-[0.98] disabled:opacity-60">
                  {deleting ? "Deleting…" : "Yes, Delete"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
